import React from 'react'
import { Form, Select, Typography } from 'antd'
import { debounce } from 'lodash'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import {
  QUESTIONNAIRE_API_SEARCH_PARAMS_EXTENSION_URL,
  QUESTIONNAIRE_DATA_FORMAT_EXTENSION_URL,
  QUESTIONNAIRE_HELPER_TEXT_EXTENSION_URL
} from 'fhir/Questionnaire/constants'
import { useDataFetch } from 'contexts/DataFetchContext'
import { useQAAssessmentActionsContext } from 'contexts/QAAssessmentActionsContext'
import { getCssSelectorForLinkId } from 'utils/helpers'
import { QuestionnaireAnswerType } from 'components/Assessments/assessmentType'
import { useQAAssessmentCommentsContext } from 'contexts/QAAssessmentCommentsContext'
import { getCommentQuestionLinkId } from 'components/QA/Assessments/helpers'
import { getCodeableConceptExtensionValue } from 'fhir/utils'
import { HalfWidthContainer, ItemWithTextDetailsContainer } from './styles'
import { extractDataFromReference, getFormattedDataOptions } from './helpers'
import { useValidateAndUpdateValues } from '../PatAssessment/hooks/useValidateAndUpdateFormValues'

export const DropdownMultiSelect = ({ question, data, extensions, dataReference }): JSX.Element => {
  const {
    answers,
    assessmentStatusState: { inQA, inQAEdit }
  } = useAssessmentContext()
  const { createCommentClickHandler, disableHover } = useQAAssessmentActionsContext()
  const validateAndUpdateFormValues = useValidateAndUpdateValues()

  const { sortedComments: comments, focusedCommentId } = useQAAssessmentCommentsContext()
  const focusedComment = comments.find((c) => c.id === focusedCommentId)
  const focusedCommentLinkId = focusedComment ? getCommentQuestionLinkId(focusedComment) : null

  const { Option } = Select

  const { setSearchQuery, setOffset } = useDataFetch()

  const defaultItemOptions = extractDataFromReference(data, dataReference)
  const formatExtension = getCodeableConceptExtensionValue(
    extensions,
    QUESTIONNAIRE_DATA_FORMAT_EXTENSION_URL
  )

  const dataFormat = formatExtension?.coding?.[0]?.code || ''
  const options = defaultItemOptions?.map((item) => {
    const { label } = getFormattedDataOptions(item, dataFormat)
    return { label }
  })

  const savedAnswer = answers[question.linkId]
  const savedAnswerArray = Array.isArray(savedAnswer)
    ? savedAnswer
    : savedAnswer
    ? [savedAnswer]
    : []
  const savedValues = savedAnswerArray.map((answer) => answer?.valueCoding?.display)
  const handleSelectChange = (value): void => {
    const newAnswers = value.map((val) => {
      const codingValue = {
        code: val,
        display: val
      }
      return codingValue
    })

    validateAndUpdateFormValues({
      type: question.type as QuestionnaireAnswerType,
      text: question.text,
      linkId: question.linkId,
      value: newAnswers
    })
  }

  const searchParamExtension = getCodeableConceptExtensionValue(
    extensions,
    QUESTIONNAIRE_API_SEARCH_PARAMS_EXTENSION_URL
  )
  const searchParam = searchParamExtension?.coding?.[0]?.code

  const handleSearch = debounce((searchValue): void => {
    if (searchParam) {
      setSearchQuery({ param: searchParam, value: searchValue })
      setOffset(0)
    }
  }, 300)

  const handleScroll = (e): void => {
    if (searchParam) {
      const threshold = 5 // 5 pixels threshold
      const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + threshold
      if (bottom) {
        setOffset((prevOffset) => prevOffset + 50)
      }
    }
  }

  const handleBlur = (): void => {
    if (searchParam) {
      setSearchQuery({ param: searchParam, value: '' })
      setOffset(0)
    }
  }

  const helperTextExtension = getCodeableConceptExtensionValue(
    extensions,
    QUESTIONNAIRE_HELPER_TEXT_EXTENSION_URL
  )
  const helperText = helperTextExtension?.coding?.[0].display

  return (
    <ItemWithTextDetailsContainer
      id={getCssSelectorForLinkId(question.linkId)}
      inQA={inQA}
      inQAEdit={inQAEdit}
      disableHover={disableHover}
      isFocused={focusedCommentLinkId === question.linkId}
      onClick={() => question.linkId && createCommentClickHandler(question.linkId)}
    >
      <HalfWidthContainer>
        <Typography.Title level={5}>{question.text}</Typography.Title>
        {helperText && <Typography.Paragraph type="secondary">{helperText}</Typography.Paragraph>}
      </HalfWidthContainer>
      <HalfWidthContainer>
        <Form.Item
          name={question.linkId}
          initialValue={savedValues}
          rules={[{ required: question.required, message: 'Please select an option' }]}
        >
          <Select
            mode="multiple"
            placeholder="Type or select an option"
            disabled={question.readOnly || undefined}
            onSearch={handleSearch}
            onBlur={handleBlur}
            onPopupScroll={handleScroll}
            showSearch={true}
            style={{ width: '100%' }}
            onChange={handleSelectChange}
          >
            {options?.map((option, index) => (
              <Option key={index} value={option.label}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </HalfWidthContainer>
    </ItemWithTextDetailsContainer>
  )
}
